/**
 * Bulk Storage Reassignment + RPO-Tier + Change History (Spec 208 — US2/US3/US4)
 *
 * Operates on the operator's SELECTED backups (row multi-select, mirroring the Plans tab):
 * reassign the destination storage account, or set the RPO tier, for the selected targets,
 * with a dry-run preview + per-entity results. Plus a change-history viewer (who/when/old→new)
 * for compliance. Placement-flexible via showBulk/showHistory. Built from @/components/ui + tokens.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 208-transparent-storage-switching
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HardDrive, Gauge, History, Loader2, Terminal, ExternalLink, Copy, Check } from 'lucide-react';
import {
  Button,
  Modal,
  Select,
  Textarea,
  Badge,
  DataTable,
  EmptyState,
} from '@/components/ui';
import {
  assignmentApi,
  BackupEntityType,
  BulkOpResponse,
  BulkItemResult,
  BackupChange,
  StoragePrepCommand,
} from '../../api/assignment';
import { PROTECTION_TIERS, tierIcon } from '../common/ProtectionTierBadge';
import { formatEastern } from '@/utils/dateFormatter';
import { storageAccountLabel } from '../../utils/storageAccountLabel';

interface Props {
  targets: Array<{ entityType: BackupEntityType; entityId: string }>;
  canManage: boolean;
  onApplied?: () => void;
  /** Render the bulk action buttons (Reassign storage / Set RPO tier). Default true. */
  showBulk?: boolean;
  /** Render the Change history button. Default true. */
  showHistory?: boolean;
}

const fmtWhen = (iso: string | null | undefined): string =>
  iso ? formatEastern(iso) : '—';

const statusVariant: Record<BulkItemResult['status'], 'success' | 'warning' | 'danger'> = {
  succeeded: 'success',
  skipped: 'warning',
  failed: 'danger',
};

/** Per-entity results table shared by both bulk modals. */
const ResultsTable: React.FC<{ result: BulkOpResponse }> = ({ result }) => (
  <div className="mt-4">
    <div className="mb-2 flex flex-wrap gap-2 text-sm">
      <Badge variant="default">{result.summary.total} total</Badge>
      <Badge variant="success">{result.summary.succeeded} ok</Badge>
      <Badge variant="warning">{result.summary.skipped} skipped</Badge>
      <Badge variant="danger">{result.summary.failed} failed</Badge>
    </div>
    <DataTable<BulkItemResult>
      minWidth="36rem"
      columns={[
        { key: 'entityId', header: 'Entity', width: '16rem', render: (r) => (
          <span className="block min-w-0">
            <span className="block truncate text-sm text-[var(--color-text-primary)]">{r.entityId}</span>
            <Badge variant="default">{r.entityType}</Badge>
          </span>
        ) },
        { key: 'status', header: 'Status', width: '7rem', sortable: false, render: (r) => <Badge variant={statusVariant[r.status]}>{r.status}</Badge> },
        { key: 'detail', header: 'Detail', sortable: false, wrap: true, render: (r) => (
          <span className="text-xs text-[var(--color-text-secondary)]">
            {r.reason ? r.reason : r.newAccount ? `${r.oldAccount ?? '—'} → ${r.newAccount}` : r.newTier ? `${r.oldTier ?? '—'} → ${r.newTier}` : ''}
            {r.grant && r.grant !== 'skipped' ? `  ·  grant: ${r.grant}` : ''}
          </span>
        ) },
      ]}
      rows={result.results}
      rowKey={(r) => `${r.entityType}:${r.entityId}`}
    />
  </div>
);

/** A read-only command line with a copy button (design-conformant; tokens only). */
const CommandLine: React.FC<{ label: string; command: string }> = ({ label, command }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the operator can select the text manually */
    }
  };
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-[var(--color-text-secondary)]">{label}</div>
      <div className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
        <code className="min-w-0 grow whitespace-pre-wrap break-words font-mono text-xs text-[var(--color-text-primary)]">{command}</code>
        <Button variant="ghost" size="sm" onClick={copy} title="Copy">
          {copied ? <Check className="h-4 w-4 text-[var(--color-status-success)]" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export const BulkStorageRpoActions: React.FC<Props> = ({ targets, canManage, onApplied, showBulk = true, showHistory = true }) => {
  const [mode, setMode] = useState<null | 'storage' | 'rpo' | 'history'>(null);
  const [acct, setAcct] = useState('');
  const [tier, setTier] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<BulkOpResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPrep, setShowPrep] = useState(false);

  const storage = useQuery({ queryKey: ['backup', 'storage-accounts'], queryFn: assignmentApi.storageAccounts, enabled: mode === 'storage' });
  const history = useQuery({ queryKey: ['backup', 'changes'], queryFn: () => assignmentApi.changes(), enabled: mode === 'history' });
  // Spec 208 — the "prepare this destination" admin command for the chosen account (the app
  // can't grant storage RBAC itself; this generates the exact command to run as an admin).
  const prep = useQuery({
    queryKey: ['backup', 'storage-prep', acct],
    queryFn: () => assignmentApi.storagePrepCommand(acct),
    enabled: mode === 'storage' && !!acct,
  });

  const close = () => { setMode(null); setResult(null); setReason(''); setAcct(''); setTier(''); setShowPrep(false); };

  const runStorage = async (dryRun: boolean) => {
    if (!acct) return;
    setBusy(true);
    try {
      const res = await assignmentApi.reassignStorage({ targetStorageAccountId: acct, selection: { ids: targets }, reason: reason || undefined, dryRun });
      setResult(res);
      if (!dryRun) onApplied?.();
    } finally { setBusy(false); }
  };
  const runTier = async (dryRun: boolean) => {
    if (!tier) return;
    setBusy(true);
    try {
      const res = await assignmentApi.assignRpoTier({ rpoTier: tier, selection: { ids: targets }, reason: reason || undefined, dryRun });
      setResult(res);
      if (!dryRun) onApplied?.();
    } finally { setBusy(false); }
  };

  return (
    <>
      {showBulk && canManage && (
        <>
          <Button variant="outline" size="sm" onClick={() => setMode('storage')} disabled={targets.length === 0} title="Reassign destination storage for the selected backups">
            <HardDrive className="h-4 w-4" /> Reassign storage ({targets.length})
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMode('rpo')} disabled={targets.length === 0} title="Set the RPO tier for the selected backups">
            <Gauge className="h-4 w-4" /> Set RPO tier ({targets.length})
          </Button>
        </>
      )}
      {showHistory && (
        <Button variant="ghost" size="sm" onClick={() => setMode('history')} title="Storage / RPO change history">
          <History className="h-4 w-4" /> Change history
        </Button>
      )}

      {/* Reassign storage */}
      <Modal isOpen={mode === 'storage'} onClose={close} title={`Reassign storage — ${targets.length} target(s)`} size="lg">
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          New backups for the {targets.length} selected target(s) will go to the chosen account, and the backup service
          principal is re-granted there. <strong>Existing recovery points are untouched</strong> — they still restore and
          prune from their original account (transparent switch).
        </p>
        <div className="space-y-3">
          <Select
            value={acct}
            onChange={(e) => setAcct(e.target.value)}
            options={[
              { value: '', label: storage.isLoading ? 'Loading accounts…' : 'Select destination account…' },
              ...(storage.data ?? []).map((s) => ({
                value: s.id,
                // account_name (display_name) via the shared helper — identity first.
                label: `${storageAccountLabel(s)}${s.envHint ? ` (${s.envHint})` : ''}`,
              })),
            ]}
          />
          {/* Spec 208 — destination provisioning. The app can't grant storage RBAC (the SP can't
              self-grant; it needs an admin), so when an account is chosen we surface the exact admin
              command to run + a one-click Azure Cloud Shell (authenticates as you). */}
          {acct && prep.data && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm">
                  <Terminal className="h-4 w-4 text-[var(--color-text-secondary)]" />
                  {prep.data.likelyProvisioned ? (
                    <Badge variant="success">already used for backups</Badge>
                  ) : (
                    <Badge variant="info">no backups here yet</Badge>
                  )}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowPrep((v) => !v)}>
                  {showPrep ? 'Hide setup' : 'Prepare destination'}
                </Button>
              </div>
              {showPrep && (
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-[var(--color-text-secondary)]">
                    A backup destination needs the backup service principal granted <strong>Storage Blob Data</strong> access.
                    The app can&apos;t grant this — do it once as an admin: click <strong>Open Azure Cloud Shell</strong> (it signs
                    you in), then run <strong>command 1</strong>. Add <strong>command 2</strong> (private endpoint) only if the
                    account is firewalled (default-deny). Self-contained — no repo needed.
                  </p>
                  <a href={prep.data.cloudShellUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4" /> Open Azure Cloud Shell
                    </Button>
                  </a>
                  <p className="text-xs text-[var(--color-text-secondary)]">🔑 {prep.data.loginHint}</p>
                  {prep.data.minimalRbacCommand && (
                    <CommandLine label="1) Grant access — run this (any shell; enough for public/reachable accounts)" command={prep.data.minimalRbacCommand} />
                  )}
                  <CommandLine label="2) Private endpoint — only if the account is firewalled (default-deny); run in Bash" command={prep.data.fullBlueprintCommand} />
                  <p className="text-xs text-[var(--color-text-secondary)]">Data-plane RBAC takes a few minutes to propagate before the first backup can write.</p>
                </div>
              )}
            </div>
          )}
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recorded in the change log)…" rows={2} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => runStorage(true)} disabled={!acct || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Preview (dry run)
            </Button>
            <Button variant="primary" size="sm" onClick={() => runStorage(false)} disabled={!acct || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply reassignment
            </Button>
          </div>
        </div>
        {result && <ResultsTable result={result} />}
      </Modal>

      {/* Set RPO tier */}
      <Modal isOpen={mode === 'rpo'} onClose={close} title={`Set RPO tier — ${targets.length} target(s)`} size="lg">
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          Sets the source RPO/protection tier (the recovery objective the command center measures at-risk against) for the
          {` ${targets.length}`} selected target(s). Existing recovery points are not altered.
        </p>
        <div className="space-y-3">
          <Select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            options={[{ value: '', label: 'Select tier…' }, ...PROTECTION_TIERS.map((t) => ({ value: t, label: `${tierIcon(t)} ${t}` }))]}
          />
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recorded in the change log)…" rows={2} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => runTier(true)} disabled={!tier || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Preview (dry run)
            </Button>
            <Button variant="primary" size="sm" onClick={() => runTier(false)} disabled={!tier || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply tier
            </Button>
          </div>
        </div>
        {result && <ResultsTable result={result} />}
      </Modal>

      {/* Change history */}
      <Modal isOpen={mode === 'history'} onClose={close} title="Storage / RPO change history" size="xl">
        {history.isLoading ? (
          <p className="px-1 py-3 text-sm text-[var(--color-text-secondary)]">Loading…</p>
        ) : (history.data ?? []).length === 0 ? (
          <EmptyState title="No changes recorded yet" />
        ) : (
          <DataTable<BackupChange>
            minWidth="44rem"
            columns={[
              { key: 'changedAt', header: 'When', width: '10rem', render: (c) => fmtWhen(c.changedAt) },
              { key: 'changeType', header: 'Type', width: '7rem', sortable: false, render: (c) => <Badge variant={c.changeType === 'storage' ? 'info' : 'default'}>{c.changeType === 'storage' ? 'storage' : 'RPO tier'}</Badge> },
              { key: 'entityId', header: 'Entity', width: '15rem', render: (c) => (
                <span className="block min-w-0"><span className="block truncate text-sm text-[var(--color-text-primary)]">{c.entityId}</span><Badge variant="default">{c.entityType}</Badge></span>
              ) },
              { key: 'change', header: 'Change', sortable: false, wrap: true, render: (c) => <span className="text-sm text-[var(--color-text-secondary)]">{c.oldValue ?? '—'} → {c.newValue ?? '—'}</span> },
              { key: 'changedByName', header: 'By', width: '9rem', sortable: false, render: (c) => <span className="text-xs text-[var(--color-text-secondary)]">{c.changedByName ?? c.changedBy ?? 'system'}</span> },
              { key: 'reason', header: 'Reason', sortable: false, wrap: true, render: (c) => <span className="text-xs text-[var(--color-text-secondary)]">{c.reason ?? ''}</span> },
            ]}
            rows={history.data ?? []}
            rowKey={(c) => c.id}
          />
        )}
      </Modal>
    </>
  );
};

export default BulkStorageRpoActions;
